import React, { useState, useEffect, useRef, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterCondition,
  CharacterFeatureSource,
  CharacterSheetInterface,
  Condition,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import CharacterConditionsButton from "@dndComponents/character/conditions/CharacterConditionsButton";
import ConditionBox from "@dndComponents/other/ConditionBox";
import LoadingBox from "@components/input/LoadingBox";

interface Props {
  char: Character;
  char_interface: CharacterSheetInterface;
  disabled?: boolean;
  shared: boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterConditions({ 
  char,
  char_interface,
  disabled = false,
  shared,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback( async () => {
    setLoading(true);
    // debugger;
    const res = await data_util.getConditions();
    setConditions(res);
    setLoading(false);
  }, []);

  const recalculated = useCallback(() => {
    // console.log("Conditions");
    setReloading(true);
    if (conditions.length === 0) {
      load();
    }
  }, [conditions, load]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
      const active_conditions = char.conditions;
      const condition_immunities = char.condition_immunities;
      const favorites = char.favorite_conditions;
      if (active_conditions.length > 0 || condition_immunities.length > 0 || favorites.length > 0) {
        load();
      }
    }
  }, [fetched, char, load, recalculated]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderConditions = (favorites: boolean) => {
    if (loading) {
      return (<LoadingBox />);
    } else {
      // debugger;
      const active_conditions = char.conditions;
      const condition_immunities = char.condition_immunities;
      const use_conditions = favorites ? conditions.filter(o => 
        char.favorite_conditions.includes(o._id)
      ) : conditions.filter(o => 
        !char.favorite_conditions.includes(o._id) &&
        (active_conditions.some(o2 => o2.source_id === o._id) ||
          condition_immunities.includes(o._id)
        )
      );
      // console.log(use_conditions);
      return use_conditions.map(cond => {
        const enabled = api.user && (char.owner_id === api.user._id || (char.campaign && char.campaign.owner_id === api.user._id));
        // const active = char.conditions.find(o => o.source_id === cond._id);
        const starred = char.favorite_conditions.includes(cond._id);
        const immune = char.condition_immunities.includes(cond._id);
        return (
          <Grid
            key={cond._id}
            size={{
              xs: 12
            }}>
            <ConditionBox
              char_int={char_interface}
              onCharChange={() => {
                char.changed();
                char_util.repopulateCharFeatureBases(char);
              }}
              disabled={!enabled}
              starred={starred}
              immune={immune}
              // active={active}
              condition={cond}
              onChange={ async (level: number) => {
                let cc = char.conditions.find(o => o.source_id === cond._id);
                if (level === 0) {
                  if (cc) {
                    char_util.removeCharFeatureBasesFor(char, cc);
                    char.conditions = char.conditions.filter(o => o.source_id !== cond._id);
                  }
                } else {
                  if (!cc) {
                    await api.loadObject(cond);
                    cc = new CharacterCondition();
                    cc.copy_source({ char, feature_source: cond });
                    char.conditions.push(cc);
                  }
                  cc.level = level;
                }
                // if (level === 0) {
                //   delete char.condition_hash[cond._id];
                // } else {
                //   if (char.condition_hash[cond._id]) {
                //     char.condition_hash[cond._id].level = level;
                //   } else {
                //     char.condition_hash[cond._id] = { level: level, source_true_id: "" };
                //   }
                // }
                char.changed();
                char_util.repopulateCharFeatureBases(char);
              }}
              onStar={() => {
                if (char.favorite_conditions.includes(cond._id)) {
                  char.favorite_conditions = char.favorite_conditions.filter(o => o !== cond._id);
                } else {
                  char.favorite_conditions.push(cond._id);
                }
                char.changed();
                char_util.repopulateCharFeatureBases(char);
              }}
              onImmune={() => {
                if (char.condition_immunities.includes(cond._id)) {
                  char.condition_immunities = char.condition_immunities.filter(o => o !== cond._id);
                } else {
                  char.condition_immunities.push(cond._id);
                }
                char.changed();
                char_util.repopulateCharFeatureBases(char);
              }}
            />
          </Grid>
        );
      });
    }
  }

  return (
    <Grid className="curved_border bg_black_transp">
      <div>
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <CharacterConditionsButton
              char={char}
              char_interface={char_interface}
              disabled={disabled}
            />
          </Grid>
          { renderConditions(true) }
          { renderConditions(false) }
        </Grid>
      </div>
    </Grid>
  );
}
